import React from "react";
import { useQueries } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Activity, TrendingUp, GitBranch, Users2, Gauge, AlertOctagon, TestTube2 } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

function Panel({ title, subtitle, icon: Icon, data, testId }) {
  const items = Array.isArray(data)
    ? data
    : data && typeof data === "object"
    ? Object.entries(data).map(([k, v]) => ({ name: k, value: typeof v === "number" ? v : (typeof v === "object" ? 0 : Number(v) || 0) }))
    : [];
  return (
    <div className="rounded-3xl bg-white shadow-card p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-ink-500 text-[11px] font-semibold uppercase tracking-[0.15em]">
        {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />} {subtitle}
      </div>
      <div className="font-heading text-lg text-ink-900 mt-1">{title}</div>
      {items.length === 0 ? (
        <div className="mt-4 text-sm text-ink-500">No data yet.</div>
      ) : (
        <div className="h-56 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={items.slice(0, 12)}>
              <CartesianGrid stroke="#E8E4DD" strokeDasharray="3 3" />
              <XAxis dataKey="name" fontSize={10} stroke="#8A847C" tickLine={false} axisLine={false} interval={0} angle={-15} height={40} textAnchor="end" />
              <YAxis fontSize={11} stroke="#8A847C" tickLine={false} axisLine={false} width={28} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E4DD" }} />
              <Bar dataKey="value" fill="#728468" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

const routes = [
  { key: "summary", url: "/admin/usage/summary", icon: Activity, subtitle: "Summary" },
  { key: "retention", url: "/admin/usage/retention", icon: TrendingUp, subtitle: "Retention" },
  { key: "funnel", url: "/admin/usage/funnel", icon: GitBranch, subtitle: "Funnel" },
  { key: "adoption", url: "/admin/usage/adoption", icon: Users2, subtitle: "Adoption" },
  { key: "performance", url: "/admin/usage/performance", icon: Gauge, subtitle: "Performance" },
  { key: "errors", url: "/admin/usage/errors", icon: AlertOctagon, subtitle: "Errors" },
  { key: "testers", url: "/admin/usage/testers", icon: TestTube2, subtitle: "Testers" },
];

export default function AdminUsage() {
  const t = useT();
  const queries = useQueries({
    queries: routes.map((r) => ({
      queryKey: [`usage-${r.key}`],
      queryFn: async () => (await api.get(r.url)).data,
      retry: false,
    })),
  });

  const download = async () => {
    try {
      const res = await api.get("/admin/usage/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "inner-compass-usage-export.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.admin")}
        title={t("nav.usage")}
        subtitle={t("admin.subtitle")}
        actions={
          <button onClick={download} data-testid="usage-export" className="rounded-full bg-white border border-border h-11 px-4 text-sm hover:shadow-card">
            {t("admin.exportCsv")}
          </button>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {routes.map((r, i) => {
          const q = queries[i];
          if (q.isLoading) return <LoadingCard key={r.key} height="h-64" />;
          if (q.isError) return <ErrorState key={r.key} message={errMsg(q.error)} onRetry={() => q.refetch()} testId={`usage-${r.key}-error`} />;
          return (
            <Panel key={r.key} subtitle={r.subtitle} title={r.subtitle} icon={r.icon} data={q.data} testId={`usage-${r.key}`} />
          );
        })}
      </div>
    </div>
  );
}
